<template>
  <cf-validated-form
    name="updateAccount"
    @valid-submit="updateAccount"
  >
    <div class="cf-flex cf-flex--column cf-flex--gap-16">
      <slot name="header">
        <h2 class="cf-title cf-title--2">
          Your account was linked!
        </h2>

        <p class="cf-subtitle cf-subtitle--2">
          We just need a little more information to finish creating your account.
        </p>
      </slot>

      <cf-phone-number-input
        v-if="merchant.userSignUpFields.primarySmsNumber && !merchant.userSignUpFields.primarySmsNumber.signupDisabled && !currentUser.primarySmsNumber"
        v-model="primarySmsNumber"
        label="Phone Number"
        name="phoneCreateAccount"
        :show-as-optional="true"
        :validations="{
          required: merchant.userSignUpFields.primarySmsNumber.isRequired
        }"
      />

      <cf-text-input
        v-if="merchant.userSignUpFields.favoriteStoreId && !merchant.userSignUpFields.favoriteStoreId.signupDisabled && !currentUser.favoriteStore.storeId"
        type="text-as-button"
        label="Favorite Store"
        name="favoriteStore"
        icon-class="fa-map-marker-alt"
        autocomplete="off"
        :model-value="favoriteStore && favoriteStore.description"
        :show-as-optional="!merchant.userSignUpFields.favoriteStoreId.isRequired"
        :validations="{
          required: merchant.userSignUpFields.favoriteStoreId.isRequired
        }"
        @click="openFavoriteStoreModal"
      />

      <template v-if="showZipField">
        <cf-country-select
          v-model="country"
          :countries="merchant.regions.map(r => r.abbreviation)"
        />

        <cf-text-input
          v-model="zip"
          type="text"
          :label="postalCodeLabel"
          name="zip"
          :mask="{ type: 'postalCode', region: country }"
          icon-class="fa-map-marker-alt"
          inputmode="numeric"
          autocomplete="postal-code"
          :show-as-optional="true"
          :validations="postalCodeValidations"
        />
      </template>

      <cf-birthday-input
        v-if="merchant.userSignUpFields.birthdayInfo && !merchant.userSignUpFields.birthdayInfo.signupDisabled && (!currentUser.dateOfBirth || missingBirthdayYear)"
        v-model="dateOfBirth"
        :include-year="!!merchant.userSignUpFields['birthdayInfo.year']"
        :required="merchant.userSignUpFields.birthdayInfo.isRequired"
      />

      <cf-checkbox-input
        v-model="termsAccepted"
        label="Terms of Use"
        name="termsAccepted"
        :validations="{
          required: true
        }"
      >
        I agree to the

        <button
          class="cf-link cf-link--brand-secondary cf-link--heavy"
          type="button"
          @click="$openModal('termsAndConditions')"
        >
          Terms & Conditions
        </button>
      </cf-checkbox-input>

      <template v-if="merchant.userSignUpFields.smsNotificationCheckbox && !merchant.userSignUpFields.smsNotificationCheckbox.signupDisabled">
        <cf-title-separator :omit-title="true" />

        <cf-switch-input
          v-model="smsOptIn"
          :show-info-icon="true"
          description="By opting into SMS messaging, you agree to receive SMS updates from the merchant including but not limited to order updates, receipts and more. Carrier charges may apply."
          label="Get Text Updates"
          name="smsOptIn"
        />
      </template>

      <p
        v-if="enrollmentOptions.marketingOptIn"
        class="cf-content cf-text-align-left"
        v-html="dynamicContent.CreateAccount.metadata.termsAndConditions"
      />

      <button
        class="cf-button cf-button--primary cf-button--lg cf-button--fill"
        :class="{ 'cf-button--loading': isLoading }"
        type="submit"
      >
        {{ dynamicContent.CreateAccount.metadata.buttonCta }}
      </button>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import { getCountryMeta } from '@/helpers/address-format';
  import { detectDefaultCountry } from '@/helpers/detect-default-country';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfCheckboxInput from '@/view/components/shared/cf-checkbox-input/index.vue';
  import CfCountrySelect from '@/view/components/shared/cf-country-select/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';
  import CfBirthdayInput from '@/view/components/shared/cf-birthday-input/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';


  export default {
    name: 'CollectAccountInfo',

    components: {
      CfValidatedForm,
      CfCheckboxInput,
      CfCountrySelect,
      CfPhoneNumberInput,
      CfTextInput,
      CfBirthdayInput,
      CfSwitchInput,
      CfTitleSeparator
    },

    data() {
      return {
        country: 'US',
        dateOfBirth: null,
        favoriteStore: null,
        isLoading: false,
        missingBirthdayYear: false,
        primarySmsNumber: null,
        smsOptIn: false,
        termsAccepted: true,
        zip: null
      };
    },

    computed: {
      ...mapGetters('content', ['dynamicContent']),
      ...mapGetters('merchant', ['enrollmentOptions', 'loyaltyOptions', 'merchant']),
      ...mapGetters('session', ['currentUser']),

      showZipField() {
        const field = this.merchant.userSignUpFields.zip;
        return field && !field.signupDisabled && !this.currentUser.zip;
      },

      postalCodeLabel() {
        return getCountryMeta(this.country).labels.zip;
      },

      postalCodeValidations() {
        const meta = getCountryMeta(this.country);
        const validations = { required: this.merchant.userSignUpFields.zip.isRequired };
        if (meta.postalCodeRegex) {
          validations.regex = meta.postalCodeRegex;
        }
        return validations;
      }
    },

    created() {
      this.country = detectDefaultCountry(this.merchant.regions.map(r => r.abbreviation));
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        const { smsNotificationCheckbox } = this.merchant.userSignUpFields;

        if ((smsNotificationCheckbox && smsNotificationCheckbox.isRequired) || this.currentUser.notificationPreferences.sms) {
          this.smsOptIn = true;
        }

        if (
          this.merchant.userSignUpFields['birthdayInfo.year']
          && this.currentUser.birthdayInfo
          && !this.currentUser.birthdayInfo.year
        ) {
          this.missingBirthdayYear = true;
        }
      },

      async updateAccount() {
        try {
          this.isLoading = true;

          const notifications = {
            email: this.enrollmentOptions.marketingOptIn,
            marketingOptIn: this.enrollmentOptions.marketingOptIn,
            sms: this.smsOptIn
          };

          const details = {};

          if (this.dateOfBirth) {
            details.dateOfBirth = this.dateOfBirth;
          }
          if (this.primarySmsNumber) {
            details.primarySmsNumber = this.primarySmsNumber;
          }
          if (this.zip) {
            details.zip = this.zip;
          }

          await this.$store.dispatch('session/updateProfile', details);
          if (this.favoriteStore) await this.$store.dispatch('session/updateUsersFavoriteStore', this.favoriteStore.storeId);
          await this.$store.dispatch('session/updateNotifications', notifications);

          if (this.loyaltyOptions.loyaltyEnrollmentEnabled) {
            await this.$store.dispatch('session/enrollInLoyalty');
          }

          this.updateAccountSuccess();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isLoading = false;
        }
      },

      updateAccountSuccess() {
        if (this.currentUser.accountStatus === 'Pending') {
          events.emit('notification', {
            type: 'success',
            message: `Welcome, ${this.currentUser.firstName}!`
          });
        }

        this.$closeModal('collectAccountInfo');

        this.$emit('success');
      },

      selectStore(store) {
        this.favoriteStore = store;
      },

      openFavoriteStoreModal() {
        this.$openModal('selectFavoriteStore', {
          selectedStoreId: this.favoriteStore?.storeId,
          onFavoriteStoreSelection: this.selectStore
        });
      }
    }
  };
</script>
